// import styles
import "./DocumentiUtenti.css";

// import componenti
import { isCessato } from "../../components/ListaUtenti";

// import pacchetti
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import format from "date-fns/format";

// import icone
import {
  FaRegTimesCircle,
  FaRegClock,
  FaExclamationTriangle,
  FaRegCheckCircle,
  FaRegCircle,
  FaPlusCircle,
  FaMinusCircle,
} from "react-icons/fa";

// import hooks
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";

// import costanti documenti (unica fonte di verità)
import { documentTypes } from "./DocUpdate";

// import generatore report PDF
import { downloadDocumentiUtentiPdf } from "../../stampe/generatori/documentiUtentiPdf";

// Giorni entro i quali un documento è considerato "in scadenza"
const GIORNI_ATTENZIONE = 30;

// Stati riportati nel report PDF dei documenti
const STATI_REPORT = ["mancante", "scaduto", "in-scadenza"];

// In questa pagina tutti i documenti sono opzionali:
// la necessità di ciascuno va decisa per singolo dipendente

// Calcola lo stato di un documento rispetto alla data di scadenza
function statoDocumento(doc, nonRichiesto) {
  if (!doc) {
    if (nonRichiesto)
      return { stato: "non-richiesto", label: "Non richiesto", scadenza: null };
    return { stato: "mancante", label: "Mancante" };
  }
  if (doc.indeterminato)
    return { stato: "ok", label: "Indeterminato", scadenza: null };

  if (!doc.scadenza)
    return { stato: "ok", label: "Nessuna scadenza", scadenza: null };

  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const scadenza = new Date(doc.scadenza);
  const giorni = Math.ceil((scadenza - oggi) / 86400000);

  if (giorni < 0)
    return { stato: "scaduto", label: "Scaduto", scadenza, giorni };
  if (giorni <= GIORNI_ATTENZIONE)
    return { stato: "in-scadenza", label: "In scadenza", scadenza, giorni };
  return { stato: "ok", label: "Valido", scadenza, giorni };
}

// Nome visualizzato dell'utente
function nomeUtente(utente) {
  if (utente.anagrafica)
    return `${utente.anagrafica.nome || ""} ${utente.anagrafica.cognome || ""}`.trim();
  return utente.displayName || utente.email || "Utente senza nome";
}

// Icona compatta dello stato di un documento, con tooltip descrittivo
function StatoIcon({ stato, scadenza, label }) {
  const data = scadenza ? format(scadenza, "dd/MM/yy") : null;

  switch (stato) {
    case "mancante":
      return (
        <span className="doc-icon mancante" title="Mancante">
          <FaRegTimesCircle />
        </span>
      );
    case "non-richiesto":
      return (
        <span className="doc-icon non-richiesto" title="Non richiesto">
          <FaRegCircle />
        </span>
      );
    case "scaduto":
      return (
        <span className="doc-icon scaduto" title={`Scaduto il ${data}`}>
          <FaExclamationTriangle />
        </span>
      );
    case "in-scadenza":
      return (
        <span className="doc-icon attenzione" title={`In scadenza il ${data}`}>
          <FaRegClock />
        </span>
      );
    default:
      return (
        <>
          <span
            className="doc-icon ok"
            title={data ? `Valido fino al ${data}` : label}
          >
            <FaRegCheckCircle />
          </span>
          {data && <span className="doc-scadenza">{data}</span>}
        </>
      );
  }
}

export default function DocumentiUtenti() {
  const { documents: users, error } = useCollection("users");
  const { updateDocument } = useFirestore("users");
  const [ricerca, setRicerca] = useState("");
  const [settoreFiltro, setSettoreFiltro] = useState("tutti");
  const [nascondiCessati, setNascondiCessati] = useState(true);
  const [soloMancanti, setSoloMancanti] = useState(false);
  const [nascondiNonRichiesti, setNascondiNonRichiesti] = useState(true);

  // State per il report PDF
  const [exportAperto, setExportAperto] = useState(false);
  const [exportSettori, setExportSettori] = useState([]);
  const [exportOrdine, setExportOrdine] = useState("persona");
  const [exportInCorso, setExportInCorso] = useState(false);
  const [exportErrore, setExportErrore] = useState(null);

  // Imposta se un documento opzionale è richiesto o meno per un dipendente
  const impostaRichiesto = async (utente, tipo, richiesto) => {
    const nonRichiesti = utente.documentiNonRichiesti || [];
    const aggiornati = richiesto
      ? nonRichiesti.filter((t) => t !== tipo)
      : [...new Set([...nonRichiesti, tipo])];
    await updateDocument(utente.id, { documentiNonRichiesti: aggiornati });
  };

  // Settori disponibili per il filtro
  const settoriDisponibili = useMemo(() => {
    if (!users) return [];
    const settori = new Set();
    users.forEach((u) => {
      const settore = u.anagrafica?.settore;
      if (settore) settori.add(settore);
    });
    return Array.from(settori).sort((a, b) => a.localeCompare(b));
  }, [users]);

  // Utenti considerati dal report: gli ex dipendenti seguono il filtro della pagina
  const utentiReport = useMemo(
    () => (users || []).filter((u) => !(nascondiCessati && isCessato(u))),
    [users, nascondiCessati],
  );

  // Settori selezionabili nel report
  const settoriReport = useMemo(() => {
    const settori = new Set();
    utentiReport.forEach((u) => {
      settori.add(u.anagrafica?.settore || "Nessun settore");
    });
    return Array.from(settori).sort((a, b) => a.localeCompare(b));
  }, [utentiReport]);

  // Dati del report: solo i documenti mancanti, scaduti o in scadenza
  const datiReport = useMemo(() => {
    const inclusi = new Set(exportSettori);
    return utentiReport
      .map((utente) => {
        const settore = utente.anagrafica?.settore || "Nessun settore";
        if (!inclusi.has(settore)) return null;

        const documenti = utente.documenti || [];
        const nonRichiesti = utente.documentiNonRichiesti || [];
        const righe = documentTypes
          .map((tipo) => {
            const doc = documenti.find((d) => d.tipo === tipo.value) || null;
            const info = statoDocumento(doc, nonRichiesti.includes(tipo.value));
            return {
              tipoLabel: tipo.label,
              stato: info.stato,
              statoLabel: info.label,
              scadenza: info.scadenza || null,
            };
          })
          .filter((r) => STATI_REPORT.includes(r.stato));

        if (righe.length === 0) return null;
        return { nome: nomeUtente(utente), settore, righe };
      })
      .filter(Boolean);
  }, [utentiReport, exportSettori]);

  // Apre il pannello di esportazione con tutti i settori selezionati
  const apriExport = () => {
    setExportSettori(settoriReport);
    setExportErrore(null);
    setExportAperto(true);
  };

  const toggleSettoreExport = (settore) => {
    setExportSettori((correnti) =>
      correnti.includes(settore)
        ? correnti.filter((s) => s !== settore)
        : [...correnti, settore],
    );
  };

  const esportaPdf = async () => {
    if (datiReport.length === 0) return;
    setExportInCorso(true);
    setExportErrore(null);
    try {
      await downloadDocumentiUtentiPdf({
        utenti: datiReport,
        settori: exportSettori,
        ordine: exportOrdine,
      });
    } catch (err) {
      console.error("Errore durante la generazione del report:", err);
      setExportErrore("Generazione del report non riuscita. Riprova.");
    } finally {
      setExportInCorso(false);
    }
  };

  // Per ogni utente costruisce la lista completa dei documenti con il relativo stato
  const utentiConDocumenti = useMemo(() => {
    if (!users) return [];
    const termine = ricerca.trim().toLowerCase();

    return users
      .filter((utente) => {
        if (nascondiCessati && isCessato(utente)) return false;
        if (
          settoreFiltro !== "tutti" &&
          utente.anagrafica?.settore !== settoreFiltro
        )
          return false;
        if (!termine) return true;
        return nomeUtente(utente).toLowerCase().includes(termine);
      })
      .map((utente) => {
        const documenti = utente.documenti || [];
        const nonRichiesti = utente.documentiNonRichiesti || [];
        const righe = documentTypes
          .map((tipo) => {
            const doc = documenti.find((d) => d.tipo === tipo.value) || null;
            const nonRichiesto = nonRichiesti.includes(tipo.value);
            return {
              tipo,
              doc,
              ...statoDocumento(doc, nonRichiesto),
            };
          })
          .filter((r) =>
            nascondiNonRichiesti ? r.stato !== "non-richiesto" : true,
          );
        const mancanti = righe.filter((r) => r.stato === "mancante").length;
        const daRinnovare = righe.filter(
          (r) => r.stato === "scaduto" || r.stato === "in-scadenza",
        ).length;
        return {
          utente,
          settore: utente.anagrafica?.settore || "Nessun settore",
          nome: nomeUtente(utente),
          righe,
          mancanti,
          daRinnovare,
        };
      })
      .filter((u) => (soloMancanti ? u.mancanti > 0 : true))
      .sort((a, b) => {
        const confronto = a.settore.localeCompare(b.settore);
        if (confronto !== 0) return confronto;
        return a.nome.localeCompare(b.nome);
      });
  }, [
    users,
    ricerca,
    settoreFiltro,
    nascondiCessati,
    soloMancanti,
    nascondiNonRichiesti,
  ]);

  // Raggruppa gli utenti per settore
  const gruppi = useMemo(() => {
    const mappa = new Map();
    utentiConDocumenti.forEach((u) => {
      if (!mappa.has(u.settore)) mappa.set(u.settore, []);
      mappa.get(u.settore).push(u);
    });
    return Array.from(mappa.entries());
  }, [utentiConDocumenti]);

  // Statistiche riepilogative
  const totaleMancanti = utentiConDocumenti.reduce(
    (acc, u) => acc + u.mancanti,
    0,
  );
  const totaleDaRinnovare = utentiConDocumenti.reduce(
    (acc, u) => acc + u.daRinnovare,
    0,
  );

  return (
    <div className="documenti-utenti">
      <div className="title-container">
        <h2 className="page-title">Documenti Dipendenti</h2>
        <div className="controls-container">
          <input
            type="text"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            placeholder="Cerca dipendente"
            className="search-bar"
          />
          <button type="button" className="btn" onClick={apriExport}>
            Esporta report PDF
          </button>
        </div>
      </div>

      <div className="riepilogo-documenti">
        <div className="riepilogo-box">
          <span className="riepilogo-numero">{utentiConDocumenti.length}</span>
          <span className="riepilogo-label">Dipendenti</span>
        </div>
        <div className="riepilogo-box allarme">
          <span className="riepilogo-numero">{totaleMancanti}</span>
          <span className="riepilogo-label">Documenti mancanti</span>
        </div>
        <div className="riepilogo-box attenzione">
          <span className="riepilogo-numero">{totaleDaRinnovare}</span>
          <span className="riepilogo-label">Da rinnovare</span>
        </div>
      </div>

      <div className="filtri-utenti">
        <label>
          <span>Settore:</span>
          <select
            value={settoreFiltro}
            onChange={(e) => setSettoreFiltro(e.target.value)}
          >
            <option value="tutti">Tutti i settori</option>
            {settoriDisponibili.map((settore) => (
              <option key={settore} value={settore}>
                {settore}
              </option>
            ))}
          </select>
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={nascondiCessati}
            onChange={(e) => setNascondiCessati(e.target.checked)}
          />
          <span>Nascondi ex dipendenti</span>
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={soloMancanti}
            onChange={(e) => setSoloMancanti(e.target.checked)}
          />
          <span>Solo con documenti mancanti</span>
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={nascondiNonRichiesti}
            onChange={(e) => setNascondiNonRichiesti(e.target.checked)}
          />
          <span>Nascondi documenti non necessari</span>
        </label>
      </div>

      {exportAperto && (
        <div className="export-panel">
          <div className="export-header">
            <h3>Esporta report documenti</h3>
            <button
              type="button"
              className="export-close"
              title="Chiudi"
              aria-label="Chiudi"
              onClick={() => setExportAperto(false)}
            >
              <FaRegTimesCircle />
            </button>
          </div>

          <p className="export-hint">
            Il report include solo i documenti mancanti, scaduti o in scadenza
            (entro {GIORNI_ATTENZIONE} giorni).
            {nascondiCessati && " Gli ex dipendenti sono esclusi."}
          </p>

          <div className="export-sezione">
            <span className="export-label">Settori da includere</span>
            <div className="export-settori">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={
                    settoriReport.length > 0 &&
                    exportSettori.length === settoriReport.length
                  }
                  onChange={(e) =>
                    setExportSettori(e.target.checked ? settoriReport : [])
                  }
                />
                <span>Tutti i settori</span>
              </label>
              {settoriReport.map((settore) => (
                <label key={settore} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={exportSettori.includes(settore)}
                    onChange={() => toggleSettoreExport(settore)}
                  />
                  <span>{settore}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="export-sezione">
            <span className="export-label">Ordine dei dati</span>
            <div className="export-ordine">
              <label className="checkbox-label">
                <input
                  type="radio"
                  name="exportOrdine"
                  checked={exportOrdine === "persona"}
                  onChange={() => setExportOrdine("persona")}
                />
                <span>Per dipendente</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="radio"
                  name="exportOrdine"
                  checked={exportOrdine === "documento"}
                  onChange={() => setExportOrdine("documento")}
                />
                <span>Per tipo di documento</span>
              </label>
            </div>
          </div>

          {exportErrore && <p className="error">{exportErrore}</p>}

          <div className="export-actions">
            <button
              type="button"
              className="btn"
              onClick={esportaPdf}
              disabled={exportInCorso || datiReport.length === 0}
            >
              {exportInCorso
                ? "Generazione..."
                : `Genera PDF (${datiReport.length} dipendenti)`}
            </button>
            {datiReport.length === 0 && (
              <span className="export-hint">
                Nessun documento da segnalare nei settori selezionati.
              </span>
            )}
          </div>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {!users && !error && <p>Caricamento...</p>}
      {users && gruppi.length === 0 && (
        <p>Nessun dipendente trovato con i filtri selezionati.</p>
      )}

      {gruppi.map(([settore, utenti]) => (
        <section key={settore} className="settore-sezione">
          <h3 className="settore-titolo">
            {settore}
            <span className="settore-count">{utenti.length}</span>
          </h3>

          <div className="documenti-grid">
            {utenti.map((u) => (
              <div key={u.utente.id} className="documento-card">
                <div className="documento-card-header">
                  <Link
                    to={`/anagraficaDipendente/${u.utente.id}`}
                    className="documento-nome"
                  >
                    {u.nome}
                  </Link>
                  <div className="documento-badge-riepilogo">
                    {u.mancanti > 0 && (
                      <span className="badge mancante">
                        {u.mancanti} mancanti
                      </span>
                    )}
                    {u.daRinnovare > 0 && (
                      <span className="badge scaduto">
                        {u.daRinnovare} da rinnovare
                      </span>
                    )}
                    {u.mancanti === 0 && u.daRinnovare === 0 && (
                      <span className="badge ok">Completo</span>
                    )}
                  </div>
                </div>

                <ul className="documento-lista">
                  {u.righe.map((r) => (
                    <li
                      key={r.tipo.value}
                      className={`documento-riga ${r.stato}`}
                    >
                      <span className="documento-tipo">{r.tipo.label}</span>
                      <span className="documento-stato">
                        <StatoIcon
                          stato={r.stato}
                          scadenza={r.scadenza}
                          label={r.label}
                        />
                        <button
                          type="button"
                          className="toggle-richiesto"
                          title={
                            r.stato === "non-richiesto"
                              ? "Rendi richiesto"
                              : "Segna come non necessario"
                          }
                          aria-label={
                            r.stato === "non-richiesto"
                              ? "Rendi richiesto"
                              : "Segna come non necessario"
                          }
                          onClick={() =>
                            impostaRichiesto(
                              u.utente,
                              r.tipo.value,
                              r.stato === "non-richiesto",
                            )
                          }
                        >
                          {r.stato === "non-richiesto" ? (
                            <FaPlusCircle />
                          ) : (
                            <FaMinusCircle />
                          )}
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
